import { PHeadline, PText, PFlex, PFlexItem, PSpinner, PIcon, PPopover } from '@porsche-design-system/components-react'
import clsx from 'clsx'
import { select, axisBottom, stack, max, scaleLinear, axisLeft, area, scaleTime, utcFormat } from 'd3'
import React from 'react'

import { ratingsColors } from '../../../utils/ratingService'
import useResizeObserver from './useResizeObserver'

import styles from './areachart.module.css'

const AreaChart = ({ data, totalRatings, startDate, endDate, loading, totalRatingsHistory }) => {
  const colors = ratingsColors
  const keys = ['one', 'two', 'three', 'four', 'five']

  const svgRef = React.useRef()
  const wrapperRef = React.useRef()
  const dimensions = useResizeObserver(wrapperRef)
  const [difference, setDifference] = React.useState(0)
  const [isNegative, setIsNegative] = React.useState(false)
  const getDate = (d) => {
    return new Date(d.date)
  }

  React.useEffect(() => {
    const diff = totalRatings - totalRatingsHistory
    if (diff > 0) {
      setDifference('+' + Math.round((diff / totalRatings) * 100))
    }
    if (diff < 0) {
      setIsNegative(true)
      setDifference('-' + Math.round(((-1 * diff) / totalRatings) * 100))
    }
  }, [totalRatingsHistory, totalRatings])

  React.useEffect(() => {
    const svg = select(svgRef.current)
    const { width, height } = dimensions || wrapperRef.current.getBoundingClientRect()

    const stackGenerator = stack().keys(keys)
    const layers = stackGenerator(data)

    const minDate = data.length > 0 ? getDate(data[0]) : null
    const maxDate = data.length > 0 ? getDate(data[data.length - 1]) : null

    const extent = [0, max(layers, (layer) => max(layer, (sequence) => sequence[1]))]

    var xScale = scaleTime().domain([minDate, maxDate]).range([0, width])

    const yScale = scaleLinear().domain(extent).range([height, 0])

    const areaGenerator = area()
      .x((sequence) => xScale(getDate(sequence.data)))
      .y0((sequence) => yScale(sequence[0]))
      .y1((sequence) => yScale(sequence[1]))

    svg
      .selectAll('.layer')
      .data(layers)
      .join('path')
      .attr('class', 'layer')
      .attr('stroke', 'white')
      .attr('fill', (layer) => {
        return colors[layer.key]
      })
      .attr('opacity', 0.8)
      .attr('d', areaGenerator)
      .call((path) => path.selectAll('.layer').attr('x', 15).attr('dx', 20).attr('y', 20).attr('dy', 50))

    // axes
    const xAxis = axisBottom(xScale).tickFormat(utcFormat('%d.%m.'))
    svg
      .select('.x-axis')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis)
      .call((g) => g.select('.domain').attr('stroke-opacity', 0.4))
      .call((g) => g.selectAll('.tick text').attr('transform', 'rotate(-45)').attr('y', 10).attr('x', -20))
      .call((g) => g.selectAll('.tick').attr('stroke-opacity', 0.4))

    const yAxis = axisLeft(yScale).tickSize(width).ticks(5)

    svg
      .select('.y-axis')
      .attr('transform', `translate(${width},0)`)
      .call(yAxis)
      .call((g) => g.select('.domain').remove())
      .call((g) => g.selectAll('.tick text').attr('transform', `translate(-${width},0)`).attr('x', -5).attr('dy', 4))
      .call((g) => g.selectAll('.tick line').attr('stroke-opacity', 0.4))
  }, [colors, data, dimensions, keys])

  const addPercentageChange = () => {
    return (
      <>
        {!isNegative && (
          <>
            <PFlexItem>
              <PIcon name='play' size='small' color='notification-success' className={styles.up} />
            </PFlexItem>
            <PFlexItem>
              <PText weight='semibold' color='notification-success'>
                {difference} %
              </PText>
            </PFlexItem>
          </>
        )}
        {isNegative && (
          <>
            <PFlexItem>
              <PIcon name='play' size='small' color='notification-error' className={styles.down} />
            </PFlexItem>
            <PFlexItem>
              <PText weight='semibold' color='notification-error'>
                {difference} %
              </PText>
            </PFlexItem>
          </>
        )}
      </>
    )
  }

  return (
    <section className={clsx(styles.graph, { [styles.loading]: loading })}>
      <PFlex justifyContent='space-between' alignItems='center' className={styles.heading}>
        <PFlexItem>
          <PFlex alignItems='center'>
            <PFlexItem>
              <PHeadline variant='headline-3' align='left'>
                {totalRatings}
              </PHeadline>
            </PFlexItem>
            {addPercentageChange()}
          </PFlex>
          <PText>
            Geamtanzahl Bewertungen
            <PPopover>
              Zur Berechnung der Entwicklung wird jeweils der ausgewählte Zeitraum mit dem, dem ausgewählten Zeitraum
              vorhergehenden, gleich langen Zeitraum verglichen.
            </PPopover>
          </PText>
        </PFlexItem>
        <PFlexItem>
          <PHeadline variant='headline-5' align='right'>
            {startDate.toLocaleDateString('de-de', { year: 'numeric', month: '2-digit', day: '2-digit' })}

            {endDate !== null &&
              ' - ' + endDate.toLocaleDateString('de-de', { year: 'numeric', month: '2-digit', day: '2-digit' })}
          </PHeadline>
          <PText align='right'>Ausgewählter Zeitraum</PText>
        </PFlexItem>
      </PFlex>

      <div ref={wrapperRef} className={styles.wrapper}>
        <svg ref={svgRef} className={styles.svg}>
          <g className='x-axis' />
          <g className='y-axis' />
        </svg>
      </div>

      <div className={styles.fields}>
        <PFlex direction='row'>
          <PFlexItem className={styles.keys}>
            <div className={styles.square} style={{ background: colors.five }} />
            <PText className={styles.field} size='x-small'>
              5 Sterne
            </PText>
          </PFlexItem>
          <PFlexItem className={styles.keys}>
            <div className={styles.square} style={{ background: colors.four }} />
            <PText className={styles.field} size='x-small'>
              4 Sterne
            </PText>
          </PFlexItem>
          <PFlexItem className={styles.keys}>
            <div className={styles.square} style={{ background: colors.three }} />
            <PText className={styles.field} size='x-small'>
              3 Sterne
            </PText>
          </PFlexItem>
          <PFlexItem className={styles.keys}>
            <div className={styles.square} style={{ background: colors.two }} />
            <PText className={styles.field} size='x-small'>
              2 Sterne
            </PText>
          </PFlexItem>
          <PFlexItem className={styles.keys}>
            <div className={styles.square} style={{ background: colors.one }} />
            <PText className={styles.field} size='x-small'>
              1 Stern
            </PText>
          </PFlexItem>
        </PFlex>
      </div>
      {loading && <PSpinner className={styles.spinner} />}
    </section>
  )
}

export default React.memo(AreaChart)
